import { useState, useEffect, useCallback } from 'react';
import { supabase, withRetry } from '../lib/supabase';
import { parseLinkedInZip } from '../lib/parseLinkedInZip';
import { inferProfile } from '../lib/profileInference';
import { useAuth } from '../contexts/AuthContext';

export function useProfile() {
  const { user, updateProfileState } = useAuth();
  const userId = user?.id;
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPositions = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('positions')
      .select('*')
      .eq('user_id', userId)
      .order('is_current', { ascending: false })
      .order('sort_order');
    if (data) setPositions(data);
  }, [userId]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  async function updateProfile(updates) {
    if (!user?.id) return { error: 'Not authenticated' };
    // Use .select() to verify the update actually affected a row
    // withRetry handles AbortError from Supabase auth init race
    const { data, error } = await withRetry(() =>
      supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()
        .single()
    );
    if (error) {
      return { error };
    }
    if (!data) {
      return { error: { message: `No profile found for user ${user.id}` } };
    }
    // Directly merge updates into React state for immediate UI response
    updateProfileState(updates);
    return { error: null };
  }

  async function saveLinkedInData({ profile, positions: posData, connections }) {
    if (!user) return { error: 'Not authenticated' };
    setLoading(true);

    try {
      // Update profile
      if (profile) {
        const profileUpdates = {
          display_name: `${profile.firstName} ${profile.lastName}`.trim(),
          headline: profile.headline,
          summary: profile.summary,
          location: profile.location,
          industry: profile.industry,
          linkedin_connected: true,
        };
        if (posData && posData.length > 0) {
          profileUpdates.current_company = posData[0].company;
          profileUpdates.current_title = posData[0].title;
        }
        const { error: profileError } = await updateProfile(profileUpdates);
        if (profileError) {
          throw new Error(`Profile update failed: ${profileError.message || profileError}`);
        }

        // Infer role/seniority/skills from headline and positions
        const inferred = inferProfile(profile.headline, posData);
        await updateProfile({
          role_family: inferred.role_family,
          seniority_band: inferred.seniority_band,
          skills: inferred.skills,
        });
      }

      // Save positions
      if (posData && posData.length > 0) {
        await withRetry(() => supabase.from('positions').delete().eq('user_id', user.id));
        const posRows = posData.map((p, i) => ({
          user_id: user.id,
          company: p.company,
          title: p.title,
          location: p.location || '',
          start_date: p.startDate || '',
          end_date: p.endDate || 'Present',
          is_current: i === 0,
          sort_order: i,
        }));
        await withRetry(() => supabase.from('positions').insert(posRows));
        await fetchPositions();
      }

      // Save connections
      if (connections && connections.length > 0) {
        await withRetry(() => supabase.from('connections').delete().eq('user_id', user.id));
        for (let i = 0; i < connections.length; i += 500) {
          const chunk = connections.slice(i, i + 500).map(c => ({
            user_id: user.id,
            first_name: c.firstName || '',
            last_name: c.lastName || '',
            company: c.company || '',
            position: c.position || '',
            email: c.email || '',
          }));
          await withRetry(() => supabase.from('connections').insert(chunk));
        }
        await updateProfile({ linkedin_connections_count: connections.length });
      }

      return { error: null };
    } catch (err) {
      return { error: err.message };
    } finally {
      setLoading(false);
    }
  }

  // Demo persona ZIP files and profile photos
  const DEMO_ZIP_MAP = {
    'tom@vouch-demo.com': '/demo-data/candidate-linkedin-export.zip',
    'tracey@vouch-demo.com': '/demo-data/voucher-linkedin-export.zip',
    'kevin@vouch-demo.com': '/demo-data/hiring-manager-linkedin-export.zip',
  };

  const DEMO_PHOTOS = {
    'tom@vouch-demo.com': '/demo-data/tom.jpeg',
    'tracey@vouch-demo.com': '/demo-data/tracey.jpeg',
    'kevin@vouch-demo.com': '/demo-data/kevin.jpeg',
  };

  // Load demo data from the real LinkedIn export ZIPs
  async function loadDemoData() {
    if (!user) return { error: 'Not authenticated' };
    setLoading(true);

    try {
      const zipUrl = DEMO_ZIP_MAP[user.email];
      if (!zipUrl) return { error: 'No demo data available for this account' };

      // Fetch and parse the real LinkedIn export ZIP
      const response = await fetch(zipUrl);
      if (!response.ok) throw new Error('Failed to fetch demo data');
      const zipData = await response.arrayBuffer();
      const { profile, positions: parsedPositions, connections: parsedConnections } = await parseLinkedInZip(zipData);

      if (!profile) throw new Error('Could not parse LinkedIn profile from ZIP');

      await saveLinkedInData({
        profile,
        positions: parsedPositions,
        connections: parsedConnections,
      });

      // Set profile photo for demo personas
      const photo = DEMO_PHOTOS[user.email];
      if (photo) {
        await updateProfile({ photo_url: photo });
      }

      // Create vouch_connections to other demo users who are already set up
      await withRetry(() => supabase.rpc('setup_demo_connections'));

      return { error: null };
    } catch (err) {
      return { error: err.message };
    } finally {
      setLoading(false);
    }
  }

  async function updateRelevanceProfile(updates) {
    const allowed = ['role_family', 'seniority_band', 'skills', 'remote_preference', 'preferred_locations'];
    const filtered = {};
    for (const key of allowed) {
      if (updates[key] !== undefined) filtered[key] = updates[key];
    }
    return updateProfile(filtered);
  }

  return { positions, loading, updateProfile, saveLinkedInData, loadDemoData, fetchPositions, updateRelevanceProfile };
}
